export type Language = 'en' | 'fr'

// Individual exchanges that can be selected (checkboxes)
export type ExchangeOption = 'newton' | 'shakepay' | 'other'

export const translations = {
  en: {
    // Navigation
    nav: {
      features: 'Features',
      pricing: 'Early Access',
      joinWaitlist: 'Join Waitlist',
    },

    // Hero Section
    hero: {
      badge: 'Coming Soon',
      headline: 'The only portfolio tracker made for',
      headlineHighlight: 'Newton & Shakepay',
      headlineEnd: 'users',
      subheadline: 'Real-time portfolio • Realized & unrealized gains • Average buy/sell price • End-of-year tax summary',
      emailPlaceholder: 'Enter your email',
      cta: 'Get Early Access',
      waitlistCount: 'Join {count}+ Canadians on the waitlist',
      trustedBy: 'Trusted by Canadian crypto investors',
    },

    // Pain Section
    pain: {
      headline: 'Still using spreadsheets to track your crypto?',
      subheadline: 'Newton CSV + Shakepay CSV = No easy way to see your actual gains',
      problems: [
        'No real-time view of your portfolio value in CAD',
        'Can\'t easily see realized vs unrealized gains',
        'Calculating average buy price manually',
        'No clear picture of your actual performance',
      ],
      solution: 'There\'s a better way',
    },

    // Features Section
    features: {
      headline: 'Everything you need to track your crypto',
      subheadline: 'Built specifically for Canadian exchanges. No more spreadsheets.',
      items: [
        {
          title: 'Real-time Portfolio in CAD',
          description: 'Track your portfolio value, average buy price, realized & unrealized gains — all in Canadian dollars. Better than any spreadsheet.',
          highlight: 'Live prices in CAD',
        },
        {
          title: 'Handles 100,000+ Transactions',
          description: 'Whether you have 50 trades or 50,000, our engine processes your entire history in seconds. No crashes, no timeouts.',
          highlight: 'Enterprise-grade performance',
        },
        {
          title: 'French Version Day One',
          description: 'Full French-Canadian interface for Quebec users. Every label, every report, every notification — en français.',
          highlight: 'Bilingue dès le jour 1',
        },
        {
          title: 'Average Buy & Sell Price',
          description: 'Instantly see your average cost per coin across all your purchases. Know exactly what price you need to break even.',
          highlight: 'Automatic calculation',
        },
        {
          title: 'Realized vs Unrealized Gains',
          description: 'Clear breakdown of what you\'ve actually made vs paper gains. No more guessing your true profit.',
          highlight: 'Crystal clear P&L',
        },
        {
          title: 'End-of-Year Tax Summary',
          description: 'When tax season comes, export your capital gains summary ready for your accountant. ACB calculated correctly.',
          highlight: 'CRA-ready reports',
        },
      ],
    },

    // Social Proof Section
    socialProof: {
      headline: 'Join the waitlist',
      subheadline: 'Be one of the first Canadians to try Tracker Capital',
      counter: {
        label: 'Canadians on the waitlist',
        cta: 'Be one of the first {count}',
      },
      testimonials: {
        empty: 'First users get priority access',
        comingSoon: 'Real testimonials coming soon',
      },
      transparency: {
        badge: 'Built in Public',
        text: 'No fake numbers. Real progress updates.',
      },
      trust: [
        { icon: 'maple', text: 'Made in Canada' },
        { icon: 'shield', text: 'Proper ACB calculation for Canadian taxes' },
        { icon: 'lock', text: 'Your data never sold · encrypted at rest · you own your CSVs' },
      ],
    },

    // Pricing/Scarcity Section
    scarcity: {
      headline: 'Be a founding member',
      subheadline: 'First 500 subscribers get an exclusive discount at launch',
      spotsLeft: '{count} spots remaining',
      features: [
        'Exclusive founding member discount',
        'Priority access at launch',
        'Direct input on features',
        'Early access to new updates',
      ],
    },

    // Final CTA Section
    finalCta: {
      headline: 'Be one of the first 500',
      subheadline: 'Get exclusive founding member discount at launch',
      emailPlaceholder: 'Enter your email',
      cta: 'Claim Your Spot',
      guarantee: 'No spam, ever. Unsubscribe anytime.',
    },

    // Footer
    footer: {
      tagline: 'The portfolio tracker that Canadian crypto investors deserve.',
      links: {
        privacy: 'Privacy Policy',
        terms: 'Terms of Service',
        contact: 'Contact',
      },
      copyright: '© {year} Tracker Capital. All rights reserved.',
      madeIn: 'Made with ❤️ in Canada',
    },

    // Exit Intent Popup
    exitPopup: {
      headline: 'Wait! Don\'t miss this',
      subheadline: 'Join now to get exclusive founding member benefits',
      emailPlaceholder: 'Your email',
      cta: 'Join Waitlist',
      dismiss: 'No thanks, I\'ll keep using spreadsheets',
    },

    // Email Form
    emailForm: {
      exchangeLabel: 'Which exchange(s) do you use?',
      exchangeLabelHint: 'Select all that apply',
      exchanges: {
        newton: 'Newton',
        shakepay: 'Shakepay',
        other: 'Other exchange(s)',
      },
      selectExchange: 'Please select at least one exchange',
      specifyExchange: 'Please specify your exchange',
      otherPlaceholder: 'Enter your exchange name(s)',
    },

    // Common
    common: {
      loading: 'Loading...',
      success: 'Success!',
      error: 'Something went wrong',
      emailError: 'Please enter a valid email',
      alreadyRegistered: 'You\'re already on the waitlist!',
      alreadyRegisteredHint: 'We can send you a magic link to update your preferences.',
      requestMagicLink: 'Send me the link',
      magicLinkSent: 'Magic link sent!',
      checkSpam: 'Check your spam/junk folder if you don\'t see it within a few minutes.',
    },
  },

  fr: {
    // Navigation
    nav: {
      features: 'Fonctionnalités',
      pricing: 'Accès anticipé',
      joinWaitlist: 'Rejoindre la liste',
    },

    // Hero Section
    hero: {
      badge: 'Bientôt disponible',
      headline: 'Le seul tracker de portefeuille conçu pour les utilisateurs',
      headlineHighlight: 'Newton & Shakepay',
      headlineEnd: '',
      subheadline: 'Portefeuille en temps réel • Gains réalisés et non réalisés • Prix d\'achat/vente moyen • Sommaire fiscal annuel',
      emailPlaceholder: 'Entrez votre courriel',
      cta: 'Obtenir un accès anticipé',
      waitlistCount: 'Rejoignez {count}+ Canadiens sur la liste d\'attente',
      trustedBy: 'Fait confiance par les investisseurs crypto canadiens',
    },

    // Pain Section
    pain: {
      headline: 'Encore des feuilles de calcul pour suivre votre crypto?',
      subheadline: 'Newton CSV + Shakepay CSV = Aucun moyen facile de voir vos vrais gains',
      problems: [
        'Pas de vue en temps réel de votre portefeuille en CAD',
        'Impossible de voir facilement gains réalisés vs non réalisés',
        'Calcul manuel du prix d\'achat moyen',
        'Aucune image claire de votre performance réelle',
      ],
      solution: 'Il y a une meilleure façon',
    },

    // Features Section
    features: {
      headline: 'Tout ce dont vous avez besoin pour suivre votre crypto',
      subheadline: 'Conçu spécifiquement pour les bourses canadiennes. Fini les feuilles de calcul.',
      items: [
        {
          title: 'Portefeuille en temps réel en CAD',
          description: 'Suivez la valeur de votre portefeuille, prix d\'achat moyen, gains réalisés et non réalisés — tout en dollars canadiens. Mieux que n\'importe quelle feuille de calcul.',
          highlight: 'Prix en direct en CAD',
        },
        {
          title: 'Gère 100 000+ transactions',
          description: 'Que vous ayez 50 trades ou 50 000, notre moteur traite tout votre historique en secondes. Pas de plantages, pas de délais d\'attente.',
          highlight: 'Performance de niveau entreprise',
        },
        {
          title: 'Version française dès le jour 1',
          description: 'Interface complète en français canadien pour les utilisateurs du Québec. Chaque étiquette, chaque rapport, chaque notification — en français.',
          highlight: 'Bilingue dès le jour 1',
        },
        {
          title: 'Prix d\'achat et vente moyen',
          description: 'Voyez instantanément votre coût moyen par crypto sur tous vos achats. Sachez exactement à quel prix vous atteignez le seuil de rentabilité.',
          highlight: 'Calcul automatique',
        },
        {
          title: 'Gains réalisés vs non réalisés',
          description: 'Répartition claire de ce que vous avez réellement gagné vs gains sur papier. Plus de devinettes sur votre vrai profit.',
          highlight: 'P&L cristallin',
        },
        {
          title: 'Sommaire fiscal de fin d\'année',
          description: 'Quand vient le temps des impôts, exportez votre sommaire de gains en capital prêt pour votre comptable. PBR calculé correctement.',
          highlight: 'Rapports prêts pour l\'ARC',
        },
      ],
    },

    // Social Proof Section
    socialProof: {
      headline: 'Rejoignez la liste d\'attente',
      subheadline: 'Soyez parmi les premiers Canadiens à essayer Tracker Capital',
      counter: {
        label: 'Canadiens sur la liste d\'attente',
        cta: 'Soyez l\'un des premiers {count}',
      },
      testimonials: {
        empty: 'Les premiers utilisateurs obtiennent un accès prioritaire',
        comingSoon: 'Vrais témoignages bientôt',
      },
      transparency: {
        badge: 'Développé publiquement',
        text: 'Pas de faux chiffres. Mises à jour réelles.',
      },
      trust: [
        { icon: 'maple', text: 'Fait au Canada' },
        { icon: 'shield', text: 'Calcul PBR correct pour les impôts canadiens' },
        { icon: 'lock', text: 'Vos données jamais vendues · chiffrées au repos · vous possédez vos CSV' },
      ],
    },

    // Pricing/Scarcity Section
    scarcity: {
      headline: 'Devenez membre fondateur',
      subheadline: 'Les 500 premiers inscrits obtiennent un rabais exclusif au lancement',
      spotsLeft: '{count} places restantes',
      features: [
        'Rabais exclusif membre fondateur',
        'Accès prioritaire au lancement',
        'Influence directe sur les fonctionnalités',
        'Accès anticipé aux mises à jour',
      ],
    },

    // Final CTA Section
    finalCta: {
      headline: 'Soyez l\'un des 500 premiers',
      subheadline: 'Obtenez un rabais exclusif de membre fondateur au lancement',
      emailPlaceholder: 'Entrez votre courriel',
      cta: 'Réserver ma place',
      guarantee: 'Pas de spam, jamais. Désabonnez-vous à tout moment.',
    },

    // Footer
    footer: {
      tagline: 'Le tracker de portefeuille que les investisseurs crypto canadiens méritent.',
      links: {
        privacy: 'Politique de confidentialité',
        terms: 'Conditions d\'utilisation',
        contact: 'Contact',
      },
      copyright: '© {year} Tracker Capital. Tous droits réservés.',
      madeIn: 'Fait avec ❤️ au Canada',
    },

    // Exit Intent Popup
    exitPopup: {
      headline: 'Attendez! Ne manquez pas ça',
      subheadline: 'Inscrivez-vous pour obtenir les avantages exclusifs de membre fondateur',
      emailPlaceholder: 'Votre courriel',
      cta: 'Rejoindre la liste',
      dismiss: 'Non merci, je garde mes feuilles de calcul',
    },

    // Email Form
    emailForm: {
      exchangeLabel: 'Quelle(s) bourse(s) utilisez-vous?',
      exchangeLabelHint: 'Sélectionnez tout ce qui s\'applique',
      exchanges: {
        newton: 'Newton',
        shakepay: 'Shakepay',
        other: 'Autre(s) bourse(s)',
      },
      selectExchange: 'Veuillez sélectionner au moins une bourse',
      specifyExchange: 'Veuillez préciser votre bourse',
      otherPlaceholder: 'Entrez le(s) nom(s) de vos bourses',
    },

    // Common
    common: {
      loading: 'Chargement...',
      success: 'Succès!',
      error: 'Une erreur est survenue',
      emailError: 'Veuillez entrer un courriel valide',
      alreadyRegistered: 'Vous êtes déjà sur la liste d\'attente!',
      alreadyRegisteredHint: 'Nous pouvons vous envoyer un lien magique pour modifier vos préférences.',
      requestMagicLink: 'Envoyez-moi le lien',
      magicLinkSent: 'Lien magique envoyé!',
      checkSpam: 'Vérifiez votre dossier spam/indésirables si vous ne le voyez pas dans quelques minutes.',
    },
  },
} as const

export type Translations = typeof translations.en
